import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI, productAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Bell, Check, CheckCheck, Sparkles, Info, AlertCircle, ShoppingBag, RefreshCw, Trash2, Plus, Package } from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  system: <Info className="w-4 h-4 text-blue-500" strokeWidth={1.5} />,
  reminder: <Bell className="w-4 h-4 text-purple-500" strokeWidth={1.5} />,
  insight: <Sparkles className="w-4 h-4 text-amber-500" strokeWidth={1.5} />,
  alert: <AlertCircle className="w-4 h-4 text-red-500" strokeWidth={1.5} />,
  refill: <Package className="w-4 h-4 text-orange-500" strokeWidth={1.5} />,
  suggestion: <ShoppingBag className="w-4 h-4 text-green-500" strokeWidth={1.5} />,
  cross_insight: <Sparkles className="w-4 h-4 text-purple-500" strokeWidth={1.5} />,
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      // Trigger smart notification generation first
      await notificationAPI.generate().catch(() => {});
      const res = await notificationAPI.getAll();
      setNotifications(res.data.notifications || []);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All marked as read');
    } catch {
      toast.error('Failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.remove(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Dismissed');
    } catch { /* ignore */ }
  };

  const handleAddProduct = async (notif) => {
    try {
      await notificationAPI.addProduct(notif.id);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      toast.success(`Added "${notif.meta?.product_name || 'product'}" to your products!`);
    } catch {
      toast.error('Failed to add product');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="notifications-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-lg mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="notif-back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600"
            style={{ transition: 'color 0.2s' }}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Dashboard
          </button>
          {unreadCount > 0 && (
            <Button
              data-testid="mark-all-read-btn"
              onClick={handleMarkAllRead}
              variant="ghost"
              size="sm"
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              <CheckCheck className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-6">
        <h1 className="text-3xl font-bold tracking-tight mb-6 animate-fade-in-up" data-testid="notif-title">
          Notifications
        </h1>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-50 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <Bell className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-gray-400">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-2 stagger-children">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                data-testid={`notification-${notif.id}`}
                className={`rounded-2xl p-4 border ${
                  notif.read
                    ? 'bg-white border-gray-100'
                    : 'bg-purple-50/50 border-purple-100'
                }`}
                style={{ transition: 'background-color 0.2s' }}
              >
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                    {ICON_MAP[notif.type] || ICON_MAP.system}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`text-sm font-semibold truncate ${notif.read ? 'text-gray-600' : 'text-gray-900'}`}>
                        {notif.title}
                      </h3>
                      {!notif.read && <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />}
                    </div>
                    <p className="text-sm text-gray-400 mt-0.5">{notif.message}</p>
                    <div className="flex items-center gap-2 mt-2.5">
                      <span className="text-xs text-gray-300">{formatTime(notif.created_at)}</span>
                      {notif.category && (
                        <span className="text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full capitalize">{notif.category}</span>
                      )}
                    </div>
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-3">
                      {notif.actionable && notif.action_type === 'add_product' && !notif.read && (
                        <button
                          data-testid={`notif-add-product-${notif.id}`}
                          onClick={() => handleAddProduct(notif)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-100 hover:bg-green-100"
                          style={{ transition: 'background-color 0.2s' }}
                        >
                          <Plus className="w-3 h-3" strokeWidth={2} />
                          Add to Products
                        </button>
                      )}
                      {!notif.read && (
                        <button
                          data-testid={`notif-mark-read-${notif.id}`}
                          onClick={() => handleMarkRead(notif.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-50 text-gray-500 text-xs font-medium hover:bg-gray-100"
                          style={{ transition: 'background-color 0.2s' }}
                        >
                          <Check className="w-3 h-3" strokeWidth={2} />
                          Done
                        </button>
                      )}
                      <button
                        data-testid={`notif-delete-${notif.id}`}
                        onClick={() => handleDelete(notif.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-gray-300 text-xs hover:text-red-400 hover:bg-red-50"
                        style={{ transition: 'all 0.2s' }}
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
